import { useMutation } from '@tanstack/react-query'
import { unitId, actionId } from '@/annotations'
import { AuthCard } from '@/components/AuthCard'
import { AuthCardBrand } from '@/components/AuthCardBrand'
import { AuthCardLead } from '@/components/AuthCardLead'
import { Button } from '@/components/Button'
import { Note } from '@/components/Note'
import { Alert } from '@/components/Alert'
import { getApiClient, ApiProblemError, type Problem } from '@/data/apiClient'
import { AUTH_BRAND, A_RESET_COPY } from '@/copy/registry'
import { FormError, RateLimitedNotice, UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'

/** `a-reset` — the stated outcome of `a-forgot`, deliberately identical whether or not the
 * account exists. "Send it again" re-issues `identityRequestPasswordReset`. */
export function AReset({ email, onHaveCode }: { email: string; onHaveCode: () => void }) {
  const resend = useMutation({
    mutationFn: async () => {
      const client = getApiClient()
      const { error } = await client.POST('/auth/password/forgot', { body: { email } })
      if (error) throw new ApiProblemError(error as Problem)
    },
  })

  const problem = resend.error instanceof ApiProblemError ? resend.error.problem : undefined

  return (
    <AuthCard unitId={unitId('a-reset')}>
      <AuthCardBrand label={AUTH_BRAND} />
      <h3 className="m-0 mb-3 text-section font-semibold">{A_RESET_COPY.heading}</h3>
      <AuthCardLead>{A_RESET_COPY.leadTemplate(email)}</AuthCardLead>

      <Alert variant="info">{A_RESET_COPY.infoNote}</Alert>
      <Note>{A_RESET_COPY.nonEnumeratingNote}</Note>

      <div className="mt-9">
        <Button data-act={actionId('nav.a-resetnew')} onClick={onHaveCode}>
          {A_RESET_COPY.submitButton}
        </Button>
      </div>

      {problem && <AResetRefusal problem={problem} />}

      <Note>
        {A_RESET_COPY.resendPrefix}
        <a
          href="#"
          data-act={actionId('session.forgotPassword')}
          onClick={(e) => { e.preventDefault(); resend.mutate() }}
          className="text-link"
        >
          {A_RESET_COPY.resendLinkLabel}
        </a>
        .
      </Note>
    </AuthCard>
  )
}

function AResetRefusal({ problem }: { problem: Problem }) {
  if (problem.refusal_kind === 'RATE_LIMITED') return <RateLimitedNotice problem={problem} />
  return isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <FormError>{problem.title}</FormError>
}
